import { useEffect, useState } from "react";
import { clsx } from "clsx";
import { Button, Popover, Checkbox } from "@mui/material";
import classes from "./ListingsDropdown.module.scss";
import { IListing, IListings } from "../../../interfaces";
import { ChevronDownIcon } from "../../../../assets/icons/ChevronDownIcon";
import { ChevronUpIcon } from "../../../../assets/icons/ChevronUpIcon";
import { CloseIcon } from "../../../../assets/icons/CloseIcon";
import { AlertIcon } from "../../../../assets/icons/AlertIcon";
import { SearchInput } from "./SearchInput";
import { useSelector } from "react-redux";
import { getSelectedListingsSelector } from "@/redux/selectors/performancePageSelector";

export const ListingsDropdown = ({
  listings,
  onSelect,
  anchorEl,
  handleClick,
  handleClose,
}: IListings & {
  onSelect: (listings: IListing[]) => void;
  anchorEl: HTMLButtonElement | null;
  handleClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  handleClose: () => void;
}) => {
  const [searchQuery, setSearchQuery] = useState("");

  const [allListing, setAllListing] = useState(listings);
  const [showMore, setShowMore] = useState(false);
  const selectedStoreListings: IListing[] = useSelector(
    getSelectedListingsSelector
  );
  const [selectedLocalListings, setSelectedLocalListings] = useState<
    IListing[]
  >(() => selectedStoreListings.filter((listing) => listing.is_listed));

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;
  const icon = open ? <ChevronUpIcon /> : <ChevronDownIcon />;

  useEffect(() => {
    setSelectedLocalListings(selectedStoreListings);
  }, [selectedStoreListings]);

  const onChange = (listing: IListing) => {
    setSelectedLocalListings((prev) => {
      if (prev.includes(listing)) {
        return prev.filter(
          (id) => id.last_live_nick !== listing.last_live_nick
        );
      }
      return [...prev, listing];
    });
  };

  const setOnlyThisListing = (listing: IListing) => {
    const listings = [listing];
    setSelectedLocalListings(listings);
  };

  const onChangeAll = () => {
    if (selectedLocalListings.length === listings.length) {
      setSelectedLocalListings([]);
    } else {
      setSelectedLocalListings(listings);
    }
  };

  const removeListing = (listing: IListing) => {
    setSelectedLocalListings(
      selectedLocalListings.filter(
        (xlisting) => xlisting.last_live_nick !== listing.last_live_nick
      )
    );
  };

  const onSearchQueryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;

    setSearchQuery(query);
    setAllListing(
      listings.filter(({ last_live_nick }) =>
        last_live_nick.toLowerCase().includes(query.toLowerCase())
      )
    );
  };

  const onResetClick = () => {
    setSearchQuery("");
    setAllListing(listings);
    setSelectedLocalListings([listings[0]]);
  };

  return (
    <>
      <Button
        aria-describedby={id}
        className={clsx(classes.listingsButton, {
          [classes.expanded]: open,
        })}
        color="secondary"
        endIcon={icon}
        onClick={handleClick}
        variant="outlined"
      >
        {selectedLocalListings.length > 1 ? (
          <div className={classes.listingWithNumber}>
            <div className={classes.listingWithNumberAmount}>
              {selectedLocalListings.length}
            </div>
            <div className={classes.listingWithNumberText}>
              Properties Selected
            </div>
          </div>
        ) : selectedLocalListings.length === 1 ? (
          <div>{selectedLocalListings[0].property_id.trim().slice(0, 20)}</div>
        ) : (
          "Choose your listing"
        )}
      </Button>
      <Popover
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        className={classes.popover}
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
      >
        <div className={classes.listingsContainer}>
          <SearchInput value={searchQuery} onChange={onSearchQueryChange} />
          {selectedLocalListings.length > 0 ? (
            <div className={classes.selectedLocalListingsContainer}>
              <div className={classes.selectedLocalListingsTitle}>
                Selected Properties
              </div>
              <div className={classes.selectedLocalListings}>
                {(!showMore
                  ? selectedLocalListings.slice(0, 2)
                  : selectedLocalListings
                ).map((listing) => (
                  <div
                    className={classes.selectedListingName}
                    key={listing.last_live_nick}
                  >
                    <div>{listing.last_live_nick}</div>
                    <CloseIcon onClick={() => removeListing(listing)} />
                  </div>
                ))}
                {selectedLocalListings.length > 2 && (
                  <div
                    className={classes.showMore}
                    onClick={() => setShowMore(!showMore)}
                  >
                    {showMore ? "See less" : "See more"}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className={classes.emptyMessage}>
              <AlertIcon />
              <div>Select at least one property</div>
            </div>
          )}
          <div className={classes.divider} />
          <div className={classes.totalCount}>
            Total{" "}
            {listings.length < 10 ? `0${listings.length}` : listings.length}
          </div>
          {searchQuery.length === 0 && (
            <div className={classes.listingItem} onClick={() => onChangeAll()}>
              <Checkbox
                className={classes.checkbox}
                checked={selectedLocalListings.length === listings.length}
                inputProps={{ "aria-label": "controlled" }}
              />
              <p className={classes.listingName}>Select all</p>
            </div>
          )}
          <div className={classes.listingItemsContainer}>
            {allListing.map((listing) => {
              return (
                <div
                  className={classes.listingItem}
                  key={listing.last_live_nick}
                  onClick={() => onChange(listing)}
                >
                  <Checkbox
                    className={classes.checkbox}
                    checked={selectedLocalListings.some(
                      (xListing) =>
                        xListing.last_live_nick === listing.last_live_nick
                    )}
                    inputProps={{ "aria-label": "controlled" }}
                  />
                  <p className={classes.listingName}>
                    {listing.last_live_nick}
                  </p>
                  <div
                    onClick={(event) => {
                      event.stopPropagation();
                      setOnlyThisListing(listing);
                    }}
                    className={classes.listingItemActions}
                  >
                    Only this
                  </div>
                </div>
              );
            })}
          </div>
          <div className={classes.footer}>
            <Button
              className={classes.resetButton}
              variant="outlined"
              color="secondary"
              onClick={onResetClick}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => onSelect(selectedLocalListings)}
            >
              Select
            </Button>
          </div>
        </div>
      </Popover>
    </>
  );
};
